import { useState } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import * as styles from './styles/styles.module.scss';
import LegacyModal from 'components/LegacyModal';
import Checkbox from '@alltrails/shared/denali/components/Checkbox';
import OutlinedTextField from '@alltrails/shared/components/OutlinedTextField';
import SelectDropdown from '../forms/SelectDropdown';
import DatePicker from '../shared/DatePicker';
import { adjustFromSameNominalTimeForUser, adjustToSameNominalTimeForUser } from '../../utils/time_helpers';
import { alertTypes, defaultAlertType } from '../../utils/alerts_helpers';
import * as formStyles from '../forms/styles/styles.module.scss';
import { FormattedMessage } from '@alltrails/shared/react-intl';

const TrailAlertModal = ({ locale, alert, showAreaFields, closeModal, handleSubmit }) => {
  // We're considering the date to be "timezone-less" so adjust it (originally UTC) to be same nominal time current admin's timezone
  const [formParams, updateFormParams] = useState({
    applyToNewTrails: false,
    showStartDate: alert && !!alert.startDate,
    showEndDate: alert && !!alert.endDate,
    ...alert,
    startDate: alert && alert.startDate ? adjustToSameNominalTimeForUser(alert.startDate) : null,
    endDate: alert && alert.endDate ? adjustToSameNominalTimeForUser(alert.endDate) : null,
    recursAnnually: alert && !!alert.recursAnnually
  });
  const [errorMessage, updateErrorMessage] = useState(null);

  const updateForm = payload => {
    updateFormParams(prevState => {
      return { ...prevState, ...payload };
    });
  };

  const handleTextChange = e => {
    const { name, value } = e.currentTarget;
    const payload = { [name]: value };

    updateForm(payload);
  };

  const handleDateChange = (attribute, date) => {
    updateErrorMessage(null);
    updateForm({ [attribute]: date });
  };

  const handleCtaClick = () => {
    // We're considering the date to be "timezone-less" so adjust it to be the same nominal value in UTC as in current admin's timezone
    const adjustedStartDate = formParams.showStartDate && formParams.startDate ? adjustFromSameNominalTimeForUser(formParams.startDate) : null;
    const adjustedEndDate = formParams.showEndDate && formParams.endDate ? adjustFromSameNominalTimeForUser(formParams.endDate) : null;
    if (adjustedStartDate && adjustedEndDate && adjustedStartDate > adjustedEndDate) {
      updateErrorMessage('Expiration must be on or after start date');
      return;
    }
    if (formParams.showStartDate && formParams.showEndDate && formParams.recursAnnually && (!adjustedStartDate || !adjustedStartDate)) {
      updateErrorMessage('Recurrent alert needs start and end dates');
      return;
    }

    let data = {
      ...formParams,
      startDate: adjustedStartDate ? adjustedStartDate.toUTCString() : null,
      endDate: adjustedEndDate ? adjustedEndDate.toUTCString() : null,
      recursAnnually: adjustedStartDate && adjustedEndDate ? formParams.recursAnnually : null
    };
    delete data.showStartDate;
    delete data.showEndDate;
    handleSubmit(data);
  };

  const onAlertTypeSelect = e => {
    const alertType = e.currentTarget.dataset.value;
    updateForm({ alertType });
  };

  const toggleApplyToNewTrails = () => {
    const applyToNewTrails = !formParams.applyToNewTrails;
    updateForm({ applyToNewTrails });
  };

  const toggleRecursAnnually = () => {
    const recursAnnually = !formParams.recursAnnually;
    updateForm({ recursAnnually });
  };

  const toggleShowDate = attribute => {
    updateForm({ [attribute]: !formParams[attribute] });
  };

  const renderDate = params => {
    const datePicker = formParams[params.showAttribute] ? (
      <>
        <DatePicker
          date={formParams[params.dateAttribute]}
          onChange={date => handleDateChange(params.dateAttribute, date)}
          locale={locale}
          abbreviateMonth
          disableFuture={false}
          disablePast={params.disablePast}
        />
        <div>{params.hint}</div>
      </>
    ) : null;
    return (
      <div className={styles.formSection}>
        <Checkbox
          className={styles.checkbox}
          id={params.showAttribute}
          testId={`checkbox-${params.showAttribute}`}
          labelElement={params.label}
          size="sm"
          selected={formParams[params.showAttribute]}
          onChange={() => toggleShowDate(params.showAttribute)}
        />
        {datePicker}
      </div>
    );
  };

  const renderRecursAnnually = () => {
    if (formParams.showStartDate && formParams.showEndDate) {
      return (
        <Checkbox
          className={styles.formSection}
          labelElement="Recurs annually"
          id="recursAnnually"
          size="sm"
          testId="checkbox-recursAnnually"
          selected={formParams.recursAnnually}
          onChange={toggleRecursAnnually}
        />
      );
    }
    return null;
  };

  return (
    <LegacyModal handleBackgroundClick={closeModal}>
      <div className={`${styles.formContainer} ${formStyles.formUploadContainer}`}>
        <div className={formStyles.clearContainer}>
          <IconButton icon={{ Component: Close }} onClick={closeModal} testId="closeIcon" title="Close" variant="flat" />
        </div>
        <>
          <div className={styles.header}>
            <div className={formStyles.title}>Trail Alert</div>
          </div>
          <div className={formStyles.form}>
            <div className={styles.formSection}>
              <OutlinedTextField label="Internal Short Name" value={formParams.shortName} changeHandler={handleTextChange} name="shortName" />
            </div>
            <div className={styles.formSection}>
              <OutlinedTextField
                label="Alert Text"
                value={formParams.content}
                changeHandler={handleTextChange}
                name="content"
                multiline
                verticalResize
                rows={3}
              />
            </div>
            <div className={styles.formSection}>
              <div>
                <FormattedMessage defaultMessage="Alert type" />
              </div>
              <SelectDropdown
                ariaLabel="trail-alert-dropdown"
                options={alertTypes}
                handleChange={onAlertTypeSelect}
                defaultItem={alertTypes.find(type => type.value === formParams.alertType) || defaultAlertType}
              />
            </div>
            {showAreaFields && (
              <Checkbox
                className={styles.formSection}
                id="applyToNewTrails"
                labelElement="Apply to new trails"
                testId="checkbox-applyToNewTrails"
                selected={formParams.applyToNewTrails}
                onChange={toggleApplyToNewTrails}
                size="sm"
              />
            )}
            {renderDate({
              dateAttribute: 'startDate',
              showAttribute: 'showStartDate',
              disablePast: false,
              label: 'Start automatically',
              hint: 'Starts at the beginning of this day, PST (UTC-8)'
            })}
            {renderDate({
              dateAttribute: 'endDate',
              showAttribute: 'showEndDate',
              disablePast: true,
              label: 'Expire automatically',
              hint: 'Expires at the end of this day, PST (UTC-8)'
            })}
            {renderRecursAnnually()}
            {errorMessage && (
              <div className={styles.formSection}>
                <b>{errorMessage}</b>
              </div>
            )}
            <div className={formStyles.buttonContainer}>
              <button className={formStyles.uploadButton} type="button" onClick={handleCtaClick}>
                <FormattedMessage defaultMessage="Submit" />
              </button>
              <button className={formStyles.cancelButton} type="button" onClick={closeModal}>
                <FormattedMessage defaultMessage="Cancel" />
              </button>
            </div>
          </div>
        </>
        )
      </div>
    </LegacyModal>
  );
};

TrailAlertModal.propTypes = {
  showAreaFields: PropTypes.bool.isRequired,
  locale: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  alert: PropTypes.object
};

export default TrailAlertModal;
